import axios from "axios";
import LocalStorageToken from "@/helpers/LocalStorageToken";
import { toastError } from "@/helpers/toastify";
import { config } from "@/config";
import stateStore from "@/store";

let showerror = true;

/** Нормальный текст ошибки: nginx/504 часто отдаёт HTML-строку вместо JSON. */
function extractErrorMessage(data: unknown, fallback: string): string {
	if (data == null) return fallback;
	if (typeof data === 'string') {
		const html = data.trim();
		if (/504|Gateway Time-?out/i.test(html)) {
			return '504 Gateway Time-out — сервер ЦИСЗ/прокси не ответил вовремя. Повторите позже.';
		}
		if (/502|Bad Gateway/i.test(html)) {
			return '502 Bad Gateway — сервер ЦИСЗ/прокси недоступен. Повторите позже.';
		}
		if (/^</.test(html)) {
			const title = html.match(/<title>([^<]+)<\/title>/i)?.[1]?.trim();
			return title || fallback;
		}
		return html.slice(0, 300) || fallback;
	}
	if (typeof data === 'object' && data !== null && 'message' in data) {
		const msg = (data as { message?: unknown }).message;
		if (typeof msg === 'string' && msg.trim()) return msg;
	}
	return fallback;
}

// Класс-обёртка над axios для запросов к серверу, с обработкой токена и ошибок
class API {
	// Токен для авторизации
	public token: LocalStorageToken;

	constructor() {
		this.token = new LocalStorageToken("token");

		axios.interceptors.response.use((response) => response, (error) => error.response);
	}

	// Получить заголовки с токеном
	getHeaders() {
		const token = this.token.get();

		if (!token) return {};

		return {
			authorization: token,
			"Access-Control-Allow-Origin": "*"
		};
	}

	/**
	 * Запрос к серверу
	 * @param path 
	 * @param opts 
	 * @param toast 
	 * @returns 
	 */
	query<T = unknown>(path: string, opts: any = {}, toast = true): Promise<T> {
		stateStore.state.load = true;

		opts.timeout = 5 * 60 * 1000;

		opts.headers = {
			...this.getHeaders(),
			...opts.headers,
		};

		opts.url = `${config.api}${path}`;

		const showError = (message: any) => {
			if (showerror && toast) {
				showerror = false;

				toastError(message);
			}
		}

		return new Promise<T>((resolve, reject) => {
			axios(opts)
				.then(response => {
					if (response && response.status) {
						if (response.status == 200 || response.status == 201) {
							showerror = true;

							resolve(response.data);
						} else if (response.status === 304) {
							// Нет тела ответа — для опроса статуса это «повторить», не успех
							showerror = true;
							resolve({
								complete: false,
								message: "Ответ из кэша (304) — повторите проверку",
								data: null,
							} as T);
						} else {
							// Если получили 401 (Unauthorized), перенаправляем на логин
							if (response.status === 401) {
								stateStore.dispatch('logout');
								return;
							}

							const message = extractErrorMessage(
								response.data,
								`Ошибка сервера: ${response.status} ${response.statusText}`,
							);

							showError(message);
							reject(message);
						}
					} else {
						reject('Ошибка подключения к серверу!');
					}
				})
				.catch((err) => {
					console.error(err);

					// Если получили 401 (Unauthorized), перенаправляем на логин
					if (err && err.status === 401) {
						stateStore.dispatch('logout');
						return;
					}

					const message = extractErrorMessage(
						err?.data ?? err,
						err?.message || 'Ошибка подключения к серверу!',
					);

					showError(message);
					reject(message);
				})
				.finally(() => {
					stateStore.state.load = false;
				})
		});
	}
}

export default new API();
